import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  fetchMyRegistrations, 
  cancelRegistration, 
  submitFeedback,
  selectMyRegistrations, 
  selectRegistrationLoading, 
  selectRegistrationError,
  selectRegistrationSuccess,
  clearRegistrationSuccess,
  clearRegistrationError
} from '../data/registrationSlice';
import { AuthService } from '../services/APIService';

const MyRegistrations = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const registrations = useSelector(selectMyRegistrations);
  const loading = useSelector(selectRegistrationLoading);
  const error = useSelector(selectRegistrationError);
  const success = useSelector(selectRegistrationSuccess);
  const isAuthenticated = AuthService.isAuthenticated();
  
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [rating, setRating] = useState(5);
  
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchMyRegistrations());
    }
  }, [dispatch, isAuthenticated]);
  
  useEffect(() => {
    // Clear success/error messages when component unmounts
    return () => {
      dispatch(clearRegistrationSuccess());
      dispatch(clearRegistrationError());
    };
  }, [dispatch]);
  
  useEffect(() => {
    if (success) {
      // Close modal if feedback was submitted successfully
      setShowFeedbackModal(false);
      
      // Clear success message after 5 seconds
      const timer = setTimeout(() => {
        dispatch(clearRegistrationSuccess());
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  const handleCancelRegistration = (registrationId) => {
    if (window.confirm(t('confirm_cancel_registration'))) {
      dispatch(cancelRegistration(registrationId));
    }
  };
  
  const openFeedbackModal = (registration) => {
    setSelectedRegistration(registration);
    setFeedbackText(registration.feedback || '');
    setRating(registration.rating || 5);
    setShowFeedbackModal(true);
  };
  
  const closeFeedbackModal = () => {
    setShowFeedbackModal(false);
    setSelectedRegistration(null);
    setFeedbackText('');
    setRating(5);
  };
  
  const handleSubmitFeedback = (e) => {
    e.preventDefault();
    if (selectedRegistration) {
      dispatch(submitFeedback({
        registrationId: selectedRegistration.id,
        feedback: feedbackText,
        rating: rating
      }));
    }
  };
  
  // Group registrations by status
  const upcoming = registrations.filter(reg => 
    new Date(reg.date) > new Date() && reg.status === 'registered'
  );
  
  const past = registrations.filter(reg => 
    new Date(reg.date) <= new Date() || reg.status === 'attended' || reg.status === 'cancelled'
  );

  return (
    <div className="bg-gray-50 min-h-screen pt-16 pb-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('my_event_registrations')}</h1>
          
          {loading && registrations.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          ) : (
            <>
              {/* Error message */}
              {error && (
                <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {registrations.length === 0 ? (
                <div className="bg-white shadow-md rounded-lg p-8 text-center">
                  <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <h2 className="mt-4 text-xl font-medium text-gray-900">{t('no_registrations')}</h2>
                  <p className="mt-2 text-base text-gray-500">{t('no_registrations_description')}</p>
                  <div className="mt-6">
                    <Link 
                      to="/events" 
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                      {t('browse_events')}
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  {/* Upcoming events */}
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('upcoming_registrations')}</h2>
                    
                    {upcoming.length === 0 ? (
                      <p className="text-gray-500 italic">{t('no_upcoming_registrations')}</p>
                    ) : (
                      <div className="space-y-4">
                        {upcoming.map(registration => (
                          <div key={registration.id} className="bg-white shadow-md rounded-lg overflow-hidden">
                            <div className="p-5">
                              <div className="flex items-center justify-between">
                                <h3 className="text-lg font-medium text-gray-900">{registration.title}</h3>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {registration.status}
                                </span>
                              </div>
                              
                              <div className="mt-2 text-sm text-gray-500">
                                <div className="flex items-center mb-1">
                                  <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <span>{formatDate(registration.date)}</span>
                                </div>
                                <div className="flex items-center mb-1">
                                  <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  <span>{registration.location}</span>
                                </div>
                                <div className="flex items-center">
                                  <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span>{t('registered_on')} {formatDate(registration.registration_date)}</span>
                                </div>
                                
                                {registration.notes && (
                                  <div className="mt-2 p-2 bg-gray-50 rounded-md">
                                    <p className="text-xs font-medium text-gray-500 mb-1">{t('your_notes')}:</p>
                                    <p className="text-sm">{registration.notes}</p>
                                  </div>
                                )}
                              </div>
                              
                              <div className="mt-4 flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-2">
                                <Link 
                                  to={`/events/${registration.event_id}`} 
                                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                  {t('view_event')}
                                </Link>
                                <button 
                                  onClick={() => handleCancelRegistration(registration.id)}
                                  className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                  {t('cancel_registration')}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Past events */}
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('past_registrations')}</h2>
                    
                    {past.length === 0 ? (
                      <p className="text-gray-500 italic">{t('no_past_registrations')}</p>
                    ) : (
                      <div className="space-y-4">
                        {past.map(registration => (
                          <div key={registration.id} className="bg-white shadow-md rounded-lg overflow-hidden">
                            <div className="p-5">
                              <div className="flex items-center justify-between">
                                <h3 className="text-lg font-medium text-gray-900">{registration.title}</h3>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                                  ${registration.status === 'attended' ? 'bg-green-100 text-green-800' : 
                                    registration.status === 'cancelled' ? 'bg-red-100 text-red-800' : 
                                    'bg-gray-100 text-gray-800'}`}
                                >
                                  {registration.status}
                                </span>
                              </div>
                              
                              <div className="mt-2 text-sm text-gray-500">
                                <div className="flex items-center mb-1">
                                  <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <span>{formatDate(registration.date)}</span>
                                </div>
                                <div className="flex items-center mb-1">
                                  <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  <span>{registration.location}</span>
                                </div>
                              </div>
                              
                              {/* Display feedback if it exists */}
                              {registration.feedback && (
                                <div className="mt-3 p-3 bg-gray-50 rounded-md">
                                  <div className="flex items-center mb-1">
                                    <p className="text-xs font-medium text-gray-500">{t('your_feedback')}:</p>
                                    <div className="ml-2 flex">
                                      {[...Array(5)].map((_, i) => (
                                        <svg 
                                          key={i}
                                          className={`h-4 w-4 ${i < registration.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                                          xmlns="http://www.w3.org/2000/svg" 
                                          viewBox="0 0 20 20" 
                                          fill="currentColor"
                                        >
                                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                      ))}
                                    </div>
                                  </div>
                                  <p className="text-sm">{registration.feedback}</p>
                                </div>
                              )}
                              
                              <div className="mt-4 flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-2">
                                <Link 
                                  to={`/events/${registration.event_id}`} 
                                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                  {t('view_event')}
                                </Link>
                                {registration.status === 'attended' && !registration.feedback && (
                                  <button 
                                    onClick={() => openFeedbackModal(registration)}
                                    className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                  >
                                    {t('provide_feedback')}
                                  </button>
                                )}
                                {registration.feedback && (
                                  <button 
                                    onClick={() => openFeedbackModal(registration)}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                  >
                                    {t('update_feedback')}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Feedback Modal */}
      {showFeedbackModal && selectedRegistration && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div 
              className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog" 
              aria-modal="true" 
              aria-labelledby="modal-headline"
            >
              <form onSubmit={handleSubmitFeedback}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-headline">
                    {t('provide_event_feedback')}
                  </h3>
                  
                  <div className="mb-4">
                    <label htmlFor="eventTitle" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('event')}
                    </label>
                    <p className="text-base text-gray-900">{selectedRegistration.title}</p>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="rating" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('rating')}
                    </label>
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          className="focus:outline-none"
                        >
                          <svg 
                            className={`h-8 w-8 ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 20 20" 
                            fill="currentColor"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('feedback')}
                    </label>
                    <textarea
                      id="feedback"
                      name="feedback"
                      rows="4"
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder={t('feedback_placeholder')}
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('submitting')}
                      </>
                    ) : t('submit_feedback')}
                  </button>
                  <button
                    type="button"
                    onClick={closeFeedbackModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Success message toast */}
      {success && (
        <div className="fixed bottom-4 right-4 z-50 bg-green-50 border-l-4 border-green-400 p-4 rounded-md shadow-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => dispatch(clearRegistrationSuccess())}
                  className="inline-flex rounded-md p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <span className="sr-only">{t('dismiss')}</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyRegistrations;
